// Handles local browser state persistence securely, ensuring text elements are completely preserved 
// during billing interruptions.

/* ==========================================================================
   MODULE 2: FORM STATE CACHE & RESTORATION (formStateCache.js)
   ========================================================================== */

export function cacheRegistrationData(payload) {
  localStorage.setItem('gcs_summary_cache', JSON.stringify(payload));
}

export function clearRegistrationCache() {
  localStorage.removeItem('gcs_summary_cache');
}

export function restoreStripeCanceledData() {
  const cachedData = localStorage.getItem('gcs_summary_cache');
  if (!cachedData) return;
  
  console.log('[Cache Engine] Stripe cancellation detected. Restoring data states...');
  const data = JSON.parse(cachedData);

  // 1. Text Fields Restoration
  const fields = {
    'studentFName': data.student.studentFName,
    'studentLName': data.student.studentLName,
    'studentZip': data.student.studentZip,
    'studentSchoolOther': data.student.studentSchool,
    'studentGenderCustom': data.student.studentGender,
    'parentFName': data.parentGuardian.parentFName,
    'parentLName': data.parentGuardian.parentLName,
    'parentPhone': data.parentGuardian.parentPhone,
    'parentEmail': data.parentGuardian.parentEmail,
    'parentRelationOther': data.parentGuardian.parentRelation,
    'secondParentFirstName': data.parentGuardian.parent2FName,
    'secondParentLastName': data.parentGuardian.parent2LName,
    'secondParentRelationOther': data.parentGuardian.secondParentRelation,
    'emergFName': data.emergencyContact.emergFName,
    'emergLName': data.emergencyContact.emergLName,
    'emergPhone': data.emergencyContact.emergPhone,
    'emergRelationOther': data.emergencyContact.emergRelation,
    'explainMed': data.medical.explainMed,
    'explainAllergy': data.medical.explainAllergies,
    'hospital': data.medical.hospital,
    'signerName': data.consentAndSignatures.signerName
  };

  Object.keys(fields).forEach(id => {
    const el = document.getElementById(id);
    if (el && fields[id] !== undefined) el.value = fields[id];
  });

  // 2. Standard Select Dropdowns
  const dropdownIds = [
    'studentType', 'studentSuffix', 'studentGrade', 'studentGender', 'studentLaptop',
    'parentRelation', 'parentAttendingBeginner', 'parent1Laptop',
    'secondParentRelation', 'parent2Laptop', 'emergRelation', 'medIssues', 'allergies'
  ];

  dropdownIds.forEach(id => {
    const el = document.getElementById(id);
    if (!el) return;
    let val = undefined;
    if (id === 'studentType') val = data.student.studentType;
    else if (id === 'studentSuffix') val = data.student.studentSuffix;
    else if (id === 'studentGrade') val = data.student.studentGrade;
    else if (id === 'studentGender') val = data.student.studentGender;
    else if (id === 'studentLaptop') val = data.student.studentNeedsLaptop;
    else if (id === 'parentRelation') val = data.parentGuardian.parentRelation;
    else if (id === 'parentAttendingBeginner') val = data.parentGuardian.parentAttendance;
    else if (id === 'parent1Laptop') val = data.parentGuardian.parent1NeedsLaptop;
    else if (id === 'parent2Laptop') val = data.parentGuardian.parent2NeedsLaptop;
    else if (id === 'secondParentRelation') val = data.parentGuardian.secondParentRelation;
    else if (id === 'emergRelation') val = data.emergencyContact.emergRelation;
    else if (id === 'medIssues') val = data.medical.medIssues;
    else if (id === 'allergies') val = data.medical.allergies;

    if (val !== undefined) {
      el.value = val;
      el.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });

  // 3. DOB Dropdowns
  if (data.student.studentDOB) {
    const dobParts = data.student.studentDOB.split('-');
    if (dobParts.length === 3) {
      if (document.getElementById('birthMonth')) document.getElementById('birthMonth').value = dobParts[0];
      if (document.getElementById('birthDay')) document.getElementById('birthDay').value = dobParts[1];
      if (document.getElementById('birthYear')) document.getElementById('birthYear').value = dobParts[2];
    }
  }

  // 4. Session & Race Checkboxes
  const sessions = { 'session1': data.student.session1, 'session2': data.student.session2, 'session3': data.student.session3, 'session4': data.student.session4 };
  Object.keys(sessions).forEach(id => {
    const cb = document.getElementById(id);
    if (cb) cb.checked = sessions[id] === 1;
  });

  if (data.student.studentRace) {
    // 1. Split the packed comma-separated string back into clean, individual choice elements
    const savedRacesArray = data.student.studentRace.split(', ').map(race => race.trim());
    console.log('[Cache Engine] Target race/ethnicity values identified for recovery:', savedRacesArray);

    // 2. Scan every checkbox item inside your custom dropdown list menu
    document.querySelectorAll('#raceDropdownMenu .race-checkbox').forEach(checkbox => {
      // If this checkbox's value matches one of the elements in our array, turn it back on!
      if (savedRacesArray.includes(checkbox.value.trim())) {
        checkbox.checked = true;
        console.log(`[Cache Engine] Restored checkbox check-state for race key token: "${checkbox.value}"`);
      } else {
        checkbox.checked = false;
      }
    });

    // 3. Trigger a programmatic change event string to update the dropdown's visible text label automatically
    const raceDropdownMenu = document.getElementById('raceDropdownMenu');
    if (raceDropdownMenu) {
      raceDropdownMenu.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  // Policies and specialized schools setups
  if (document.getElementById('policies') && data.consentAndSignatures.readPolicies) {
    document.getElementById('policies').checked = ['1', 'true', 'yes'].includes(data.consentAndSignatures.readPolicies.toString().toLowerCase());
  }

  if (data.parentGuardian.parent1SessionDates) {
    const d1 = data.parentGuardian.parent1SessionDates.split(', ');
    document.querySelectorAll('input[name="parent1ClassDates"]').forEach(cb => cb.checked = d1.includes(cb.value));
  }
  if (data.parentGuardian.parent2SessionDates) {
    const d2 = data.parentGuardian.parent2SessionDates.split(', ');
    document.querySelectorAll('input[name="parent2ClassDates"]').forEach(cb => cb.checked = d2.includes(cb.value));
  }

  const schoolButton = document.getElementById('schoolDropdownButton');
  if (data.student.studentSchool && schoolButton?.querySelector('span')) {
    schoolButton.querySelector('span').textContent = data.student.studentSchool;
    if (document.getElementById('studentSchool')) document.getElementById('studentSchool').value = data.student.studentSchool;
  }
}

export function cleanUrlAddressHistory() {
  if (window.history && window.history.replaceState) {
    const cleanUrl = window.location.protocol + "//" + window.location.host + window.location.pathname;
    window.history.replaceState({ path: cleanUrl }, "", cleanUrl);
  }
}
